import { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  MenuItem
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, signUp } from '../../api/authService';
import { useUser } from '../../context/UserContext';

function SignInForm() {
  // Tab state: 0 for Sign In, 1 for Sign Up
  const [tabValue, setTabValue] = useState(0);

  // Sign In form fields
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Error and success states
  const [error, setError] = useState(null);
  const [signInErrors, setSignInErrors] = useState({});

  // Sign Up form fields using inputFields object
  const [inputFields, setInputFields] = useState({
    orgId: "1",
    memberType: "parent",
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    streetAddress: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    postalCode: ""
  });

  // Hooks
  const { updateUser } = useUser();
  const { signIn: signInContext, accessToken, handleLogin } = useAuth();
  const navigate = useNavigate();

  // Validation errors
  const [signUpErrors, setSignUpErrors] = useState({});

  // Effects
  useEffect(() => {
    if (accessToken) {
      navigate('/EventTypesForm');
    }
  }, [accessToken, navigate]);

  useEffect(() => {
    inputFields.streetAddress = (inputFields.streetAddress1.trim() + " " + inputFields.streetAddress2.trim()).trim();
  }, [inputFields.streetAddress1, inputFields.streetAddress2]);

  // Utility functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateZip = (zip) => {
    const zipRegex = /^\d{5}$/;
    return zipRegex.test(zip);
  };

  const validateMobile = (mobile) => {
    const mobileRegex = /^\d{10}$/;
    return mobileRegex.test(mobile.replace(/\D/g, ''));
  };

  const validateSignUpForm = () => {
    const errors = {};

    // Validate required fields
    if (!inputFields.firstName.trim()) {
      errors.firstName = 'Parent First Name is required';
    }
    if (!inputFields.lastName.trim()) {
      errors.lastName = 'Parent Last Name is required';
    }
    if (!inputFields.email || !validateEmail(inputFields.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!inputFields.mobileNumber || !validateMobile(inputFields.mobileNumber)) {
      errors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }
    if (!inputFields.streetAddress1.trim()) {
      errors.streetAddress1 = 'Address Line 1 is required';
    }
    if (!inputFields.city.trim()) {
      errors.city = 'City is required';
    }
    if (!inputFields.state) {
      errors.state = 'State is required';
    }
    if (!inputFields.postalCode || !validateZip(inputFields.postalCode)) {
      errors.postalCode = 'Please enter a valid 5-digit zip code';
    }

    return errors;
  };

  // Event handlers
  const handleTabChange = (_, newValue) => {
    setError(null);
    setTabValue(newValue);
  };

  const handleSignUpChange = (e) => {
    const newForm = { ...inputFields };
    newForm[e.target.name] = e.target.value;
    setInputFields(newForm);
  };


  // Sign In submission
  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = {};
    if (!signInEmail || !validateEmail(signInEmail)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!signInPassword) {
      errors.password = 'Password cannot be empty';
    }

    setSignInErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        const data = await signIn(signInEmail, signInPassword);
        signInContext(data.accessToken);
        updateUser("ttl", data.ttl);
        handleLogin();
        navigate('/EventTypesForm');
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Sign Up submission
  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate form
    const errors = validateSignUpForm();
    setSignUpErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      const { response, data } = await signUp(inputFields);

      if (response.ok) {
        updateUser("newMember", true);
        updateUser("email", inputFields.email);
        updateUser("mobileNumber", inputFields.mobileNumber);
        updateUser("existingHouseholdLastName", data.existingHouseholdLastName);
        inputFields.postalCode = data.postalCode;
        updateUser("inputFields", inputFields);

        if (data.householdAddressExists === "true") {
          navigate("/SameHousehold");
        } else {
          navigate("/ValidateOTP");
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
  };



  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
      }}
    >
      <Paper 
        elevation={8} 
        sx={{ 
          p: 5, 
          maxWidth: 480, 
          width: '100%',
          borderRadius: 3,
          background: '#ffffff',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        {/* Header Section */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
            }}
          >
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              <img src="https://membership.cmdfw.org/images/2025/chinmaya-orange-logo.png"></img>
            </Typography>
          </Box>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: '#2c3e50',
              textAlign: 'center',
              mb: 1
            }}
          >
            Events Portal
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#7f8c8d',
              textAlign: 'center',
              mb: 3
            }}
          >
            Secure access to your account
          </Typography>
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              width: '100%',
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#7f8c8d',
                '&.Mui-selected': {
                  color: '#667eea'
                }
              }
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Create Account" />
          </Tabs>
        </Box>

        {/* Sign In Tab */}
        {tabValue === 0 && (
          <Box component="form" onSubmit={handleSignInSubmit} noValidate>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              error={!!signInErrors.email}
              helperText={signInErrors.email}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea'
                }
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              margin="normal"
              value={signInPassword}
              onChange={(e) => setSignInPassword(e.target.value)}
              error={!!signInErrors.password}
              helperText={signInErrors.password}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#7f8c8d' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              size="large"
              sx={{ 
                mt: 2,
                mb: 3,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Sign In Securely
            </Button>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#e74c3c'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Link 
                    to="/ForgotUsername" 
                    style={{ 
                      color: '#667eea', 
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    Forgot Username?
                  </Link>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Link 
                    to="/ForgotPassword" 
                    style={{ 
                      color: '#667eea', 
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Sign Up Tab */}
        {tabValue === 1 && (
          <Box component="form" onSubmit={handleSignUpSubmit} noValidate>
            {/* Center Selection */}
            <FormControl 
              variant="outlined" 
              fullWidth 
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea'
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#667eea'
                }
              }}
            >
              <InputLabel>Select a Center</InputLabel>
              <Select
                required
                name="orgId"
                label="Select a Center"
                value="1"
              >
                <MenuItem value="1">CMDFW</MenuItem>
              </Select>
            </FormControl>

            {/* Personal Information */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Parent First Name"
                  name="firstName"
                  variant="outlined"
                  fullWidth
                  value={inputFields.firstName}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.firstName}
                  helperText={signUpErrors.firstName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Parent Last Name"
                  name="lastName"
                  variant="outlined"
                  fullWidth
                  value={inputFields.lastName}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.lastName}
                  helperText={signUpErrors.lastName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  type="tel"
                  label="Parent Mobile Phone"
                  name="mobileNumber"
                  variant="outlined"
                  fullWidth
                  value={inputFields.mobileNumber}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.mobileNumber}
                  helperText={signUpErrors.mobileNumber}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="email"
                  fullWidth
                  required
                  label="Parent Email Address"
                  variant="outlined"
                  name="email"
                  value={inputFields.email}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.email}
                  helperText={signUpErrors.email}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Address Line 1"
                  name="streetAddress1"
                  variant="outlined"
                  fullWidth
                  value={inputFields.streetAddress1}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.streetAddress1}
                  helperText={signUpErrors.streetAddress1}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Address Line 2 (Optional)"
                  name="streetAddress2"
                  variant="outlined"
                  fullWidth
                  value={inputFields.streetAddress2}
                  onChange={handleSignUpChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="City"
                  name="city"
                  variant="outlined"
                  fullWidth
                  value={inputFields.city}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.city}
                  helperText={signUpErrors.city}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  variant="outlined"
                  error={!!signUpErrors.state}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                >
                  <InputLabel>State</InputLabel>
                  <Select
                    value={inputFields.state}
                    required
                    onChange={handleSignUpChange}
                    name="state"
                    label="State"
                  >
                    <MenuItem value="AL">Alabama</MenuItem>
                    <MenuItem value="AK">Alaska</MenuItem>
                    <MenuItem value="AZ">Arizona</MenuItem>
                    <MenuItem value="AR">Arkansas</MenuItem>
                    <MenuItem value="CA">California</MenuItem>
                    <MenuItem value="CO">Colorado</MenuItem>
                    <MenuItem value="CT">Connecticut</MenuItem>
                    <MenuItem value="DE">Delaware</MenuItem>
                    <MenuItem value="FL">Florida</MenuItem>
                    <MenuItem value="GA">Georgia</MenuItem>
                    <MenuItem value="HI">Hawaii</MenuItem>
                    <MenuItem value="ID">Idaho</MenuItem>
                    <MenuItem value="IL">Illinois</MenuItem>
                    <MenuItem value="IN">Indiana</MenuItem>
                    <MenuItem value="IA">Iowa</MenuItem>
                    <MenuItem value="KS">Kansas</MenuItem>
                    <MenuItem value="KY">Kentucky</MenuItem>
                    <MenuItem value="LA">Louisiana</MenuItem>
                    <MenuItem value="ME">Maine</MenuItem>
                    <MenuItem value="MD">Maryland</MenuItem>
                    <MenuItem value="MA">Massachusetts</MenuItem>
                    <MenuItem value="MI">Michigan</MenuItem>
                    <MenuItem value="MN">Minnesota</MenuItem>
                    <MenuItem value="MS">Mississippi</MenuItem>
                    <MenuItem value="MO">Missouri</MenuItem>
                    <MenuItem value="MT">Montana</MenuItem>
                    <MenuItem value="NE">Nebraska</MenuItem>
                    <MenuItem value="NV">Nevada</MenuItem>
                    <MenuItem value="NH">New Hampshire</MenuItem>
                    <MenuItem value="NJ">New Jersey</MenuItem>
                    <MenuItem value="NM">New Mexico</MenuItem>
                    <MenuItem value="NY">New York</MenuItem>
                    <MenuItem value="NC">North Carolina</MenuItem>
                    <MenuItem value="ND">North Dakota</MenuItem>
                    <MenuItem value="OH">Ohio</MenuItem>
                    <MenuItem value="OK">Oklahoma</MenuItem>
                    <MenuItem value="OR">Oregon</MenuItem>
                    <MenuItem value="PA">Pennsylvania</MenuItem>
                    <MenuItem value="RI">Rhode Island</MenuItem>
                    <MenuItem value="SC">South Carolina</MenuItem>
                    <MenuItem value="SD">South Dakota</MenuItem>
                    <MenuItem value="TN">Tennessee</MenuItem>
                    <MenuItem value="TX">Texas</MenuItem>
                    <MenuItem value="UT">Utah</MenuItem>
                    <MenuItem value="VT">Vermont</MenuItem>
                    <MenuItem value="VA">Virginia</MenuItem>
                    <MenuItem value="WA">Washington</MenuItem>
                    <MenuItem value="WV">West Virginia</MenuItem>
                    <MenuItem value="WI">Wisconsin</MenuItem>
                    <MenuItem value="WY">Wyoming</MenuItem>
                  </Select>
                  {signUpErrors.state && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}>
                      {signUpErrors.state}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Zip Code"
                  name="postalCode"
                  variant="outlined"
                  fullWidth
                  value={inputFields.postalCode}
                  onChange={handleSignUpChange}
                  error={!!signUpErrors.postalCode}
                  helperText={signUpErrors.postalCode}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              size="large"
              sx={{ 
                mt: 4,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Create Account & Verify Email
            </Button>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#e74c3c'
                  }
                }}
              >
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #ecf0f1', textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#95a5a6', fontSize: '0.8rem' }}>
            © 2024 Events Portal. All rights reserved.
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#95a5a6', fontSize: '0.75rem', mt: 1 }}>
            Chinmaya Saaket, 17701 Davenport Road, Dallas Texas 75252 
            Chinmaya Chitrakoot, 900 N Beltline Road, Irving, TX 75061
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default SignInForm;